import { Trans, useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { IssueTimelineDto } from '@issuemanager/api-client';
import { EditableActionFields } from '@issuemanager/constants';
import { extractEmailFromUserString } from '@issuemanager/apps/client/utils';
import { TimelineEventTypography } from './timeline-event-typography';
import { IssueTimelineAttachmentsEvents } from './issue-timeline-attachments-events';

interface ActionTimelineEventDetailsProps {
  event: IssueTimelineDto;
}

export function ActionTimelineEventDetails({
  event,
}: ActionTimelineEventDetailsProps) {
  const { t } = useTranslation('common');
  const { editedField, editedFrom, editedTo } = event.bodyData.parameters;

  const editedFromData = extractEmailFromUserString(editedFrom?.value);
  const editedToData = extractEmailFromUserString(editedTo?.value);

  return (
    <>
      {editedField?.value === EditableActionFields.TITLE && (
        <TimelineEventTypography data-testid='action-title-edited-timeline-event'>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionTitleChanged'
            values={{
              from: editedFrom?.value,
              to: editedTo?.value,
            }}
          />
        </TimelineEventTypography>
      )}

      {editedField?.value === EditableActionFields.DESCRIPTION && (
        <TimelineEventTypography data-testid='action-description-edited-timeline-event'>
          <Trans t={t} i18nKey='issueTimeline.actionDescriptionChanged' />
        </TimelineEventTypography>
      )}

      {editedField?.value === EditableActionFields.SEQUENCE &&
        editedFrom?.value &&
        editedTo?.value && (
          <TimelineEventTypography data-testid='action-sequence-edited-timeline-event'>
            <Trans
              t={t}
              i18nKey='issueTimeline.actionReordered'
              values={{
                from: editedFrom.value,
                to: editedTo.value,
              }}
            />
          </TimelineEventTypography>
        )}

      {editedField?.value === EditableActionFields.DUE_DATE && (
        <TimelineEventTypography data-testid='action-due-date-timeline-event'>
          <Trans
            t={t}
            i18nKey='issueTimeline.actionDueDateChanged'
            values={{
              from: editedFrom?.value || '-',
              to: editedTo?.value || '-',
            }}
          />
        </TimelineEventTypography>
      )}

      {editedField?.value === EditableActionFields.ACTION_OWNER && (
        <Box data-testid='action-owner-edited-timeline-event'>
          {editedFromData && editedToData ? (
            <>
              <TimelineEventTypography>
                <Trans
                  t={t}
                  i18nKey='issueTimeline.actionOwnerChanged'
                  values={{
                    name: editedToData.name,
                    email: editedToData.email,
                  }}
                />
              </TimelineEventTypography>
              <TimelineEventTypography>
                <Trans
                  t={t}
                  i18nKey='issueTimeline.actionOwnerRemoved'
                  values={{
                    name: editedFromData.name,
                    email: editedFromData.email,
                  }}
                />
              </TimelineEventTypography>
            </>
          ) : (
            <>
              {editedFromData && (
                <TimelineEventTypography>
                  <Trans
                    t={t}
                    i18nKey='issueTimeline.actionOwnerRemoved'
                    values={{
                      name: editedFromData.name,
                      email: editedFromData.email,
                    }}
                  />
                </TimelineEventTypography>
              )}
              {editedToData && (
                <TimelineEventTypography>
                  <Trans
                    t={t}
                    i18nKey='issueTimeline.issueOwnerAdded'
                    values={{
                      name: editedToData.name,
                      email: editedToData.email,
                    }}
                  />
                </TimelineEventTypography>
              )}
            </>
          )}
        </Box>
      )}

      {editedField?.value === EditableActionFields.ACTION_APPROVER && (
        <Box data-testid='action-approver-edited-timeline-event'>
          {editedFromData && editedToData ? (
            <>
              <TimelineEventTypography>
                <Trans
                  t={t}
                  i18nKey='issueTimeline.actionApproverChanged'
                  values={{
                    name: editedToData.name,
                    email: editedToData.email,
                  }}
                />
              </TimelineEventTypography>
              <TimelineEventTypography>
                <Trans
                  t={t}
                  i18nKey='issueTimeline.actionApproverRemoved'
                  values={{
                    name: editedFromData.name,
                    email: editedFromData.email,
                  }}
                />
              </TimelineEventTypography>
            </>
          ) : (
            <>
              {editedFromData && (
                <TimelineEventTypography>
                  <Trans
                    t={t}
                    i18nKey='issueTimeline.actionApproverRemoved'
                    values={{
                      name: editedFromData.name,
                      email: editedFromData.email,
                    }}
                  />
                </TimelineEventTypography>
              )}
              {editedToData && (
                <TimelineEventTypography>
                  <Trans
                    t={t}
                    i18nKey='issueTimeline.issueApproverAdded'
                    values={{
                      name: editedToData.name,
                      email: editedToData.email,
                    }}
                  />
                </TimelineEventTypography>
              )}
            </>
          )}
        </Box>
      )}

      {editedField?.value === EditableActionFields.ATTACHMENTS && (
        <IssueTimelineAttachmentsEvents
          attachments={event.bodyData.parameters.attachments?.value ?? []}
        />
      )}
    </>
  );
}
